"use client";

import { c as _c } from "react/compiler-runtime";
import { useRollbar } from "@rollbar/react";
import { useEffect } from "react";
import { useMe } from "@/hooks/useMe";
import { useRollbarEnabled } from "./RollbarProvider";

/**
 * Inner component that uses useRollbar() - only rendered when Rollbar is enabled.
 */
function RollbarUserContextInner() {
  const $ = _c(6);
  const rollbar = useRollbar();
  const {
    me,
    userId,
    isLoading
  } = useMe();
  let t0;
  let t1;
  if ($[0] !== isLoading || $[1] !== me || $[2] !== rollbar || $[3] !== userId) {
    t0 = () => {
      if (!rollbar || isLoading) {
        return;
      }
      if (me && userId) {
        const {
          firstName,
          lastName,
          email
        } = me.data.attributes;
        rollbar.configure({
          payload: {
            person: {
              id: userId,
              username: firstName && lastName ? `${firstName} ${lastName}`.trim() : undefined,
              email
            }
          }
        });
      } else {
        rollbar.configure({
          payload: {
            person: undefined
          }
        });
      }
    };
    t1 = [rollbar, me, userId, isLoading];
    $[0] = isLoading;
    $[1] = me;
    $[2] = rollbar;
    $[3] = userId;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
  return null;
}

/**
 * RollbarUserContext - Sets user context in Rollbar when authenticated.
 *
 * Must be a child of RollbarProvider. When Rollbar is not configured
 * (missing access token), this component safely renders nothing without
 * attempting to use the Rollbar context.
 *
 * @see https://docs.rollbar.com/docs/nextjs
 */
export function RollbarUserContext() {
  const $ = _c(1);
  const isEnabled = useRollbarEnabled();
  if (!isEnabled) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <RollbarUserContextInner />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}